import { supabase } from '@/lib/supabase';

/* ------------------------------------------------------------------ */
/* Company Settings -- time zone + date format preference              */
/*                                                                      */
/* Stored in the generic `settings` table (category 'CompanySettings') */
/* as two plain-text rows. src/lib/dates.ts caches whatever this loads  */
/* so every date/time shown across the app -- payslips, reports, fleet  */
/* capacity, driver portal -- stays in lock-step without each screen    */
/* fetching it separately.                                              */
/* ------------------------------------------------------------------ */

export interface DateLocaleSettings {
  /** IANA time zone, e.g. 'Pacific/Auckland'. Daylight saving is handled
   *  automatically by Intl.DateTimeFormat for any IANA zone -- no separate
   *  DST setting is needed. */
  timezone: string;
  /** Display order for dates: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD'. */
  dateFormat: string;
}

export const DEFAULT_DATE_LOCALE: DateLocaleSettings = {
  timezone: 'Pacific/Auckland',
  dateFormat: 'DD/MM/YYYY',
};

export const TIMEZONE_OPTIONS: { value: string; label: string }[] = [
  { value: 'Pacific/Auckland', label: 'Auckland (New Zealand)' },
  { value: 'Pacific/Chatham', label: 'Chatham Islands (New Zealand)' },
  { value: 'Australia/Sydney', label: 'Sydney (Australia)' },
  { value: 'Australia/Brisbane', label: 'Brisbane (Australia)' },
  { value: 'Australia/Perth', label: 'Perth (Australia)' },
  { value: 'UTC', label: 'UTC' },
];

export const DATE_FORMAT_OPTIONS: { value: string; label: string; example: string }[] = [
  { value: 'DD/MM/YYYY', label: 'Day/Month/Year (NZ)', example: '31/12/2026' },
  { value: 'MM/DD/YYYY', label: 'Month/Day/Year (US)', example: '12/31/2026' },
  { value: 'YYYY-MM-DD', label: 'Year-Month-Day (ISO)', example: '2026-12-31' },
];

const NAME_TIMEZONE = 'company.timezone';
const NAME_DATE_FORMAT = 'company.date_format';
const NAME_COMPANY_NUMBER = 'company.company_number';
const CATEGORY = 'CompanySettings';

export const loadDateLocaleSettings = async (
  tenantId?: string | null,
): Promise<DateLocaleSettings> => {
  let q = supabase.from('settings').select('name, value').in('name', [NAME_TIMEZONE, NAME_DATE_FORMAT]);
  if (tenantId) q = q.eq('tenant_id', tenantId);
  const { data, error } = await q;
  if (error || !data) return { ...DEFAULT_DATE_LOCALE };
  const map = Object.fromEntries(data.map((r) => [r.name, r.value]));
  return {
    timezone: map[NAME_TIMEZONE] || DEFAULT_DATE_LOCALE.timezone,
    dateFormat: map[NAME_DATE_FORMAT] || DEFAULT_DATE_LOCALE.dateFormat,
  };
};

const upsertSetting = async (
  name: string,
  value: string,
  label: string,
  description: string,
  tenantId: string | null,
): Promise<void> => {
  const { data: existing } = await supabase.from('settings').select('id').eq('name', name).maybeSingle();
  if (existing) {
    const { error } = await supabase.from('settings').update({ value }).eq('name', name);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('settings').insert([
      {
        name,
        value,
        category: CATEGORY,
        label,
        description,
        type: 'text',
        sort_order: 10,
        is_active: true,
        tenant_id: tenantId,
      },
    ]);
    if (error) throw error;
  }
};

export const saveDateLocaleSettings = async (
  s: DateLocaleSettings,
  tenantId: string | null,
): Promise<void> => {
  await upsertSetting(NAME_TIMEZONE, s.timezone, 'Time Zone', 'Used for every date/time shown across the app.', tenantId);
  await upsertSetting(
    NAME_DATE_FORMAT,
    s.dateFormat,
    'Date Format',
    'How dates are displayed throughout the app.',
    tenantId,
  );
};

export const loadCompanyNumber = async (tenantId?: string | null): Promise<string> => {
  let q = supabase.from('settings').select('value').eq('name', NAME_COMPANY_NUMBER);
  if (tenantId) q = q.eq('tenant_id', tenantId);
  const { data } = await q.maybeSingle();
  return data?.value || '';
};

export const saveCompanyNumber = async (value: string, tenantId: string | null): Promise<void> => {
  await upsertSetting(
    NAME_COMPANY_NUMBER,
    value,
    'Company / NZBN Number',
    'Shown on payment summaries, claims and reports.',
    tenantId,
  );
};
